import { useState } from 'react'
import { FiX, FiLoader } from 'react-icons/fi'
import toast from 'react-hot-toast'

export default function CreateWebsiteModal({ isOpen, onClose, onSubmit, categories }) {
  const [step, setStep] = useState(1) // 1 = category, 2 = name
  const [selectedCategory, setSelectedCategory] = useState('')
  const [websiteName, setWebsiteName] = useState('')
  const [loading, setLoading] = useState(false)

  const handleCategorySelect = (category) => {
    setSelectedCategory(category)
    setStep(2)
  }

  const handleCreateWebsite = async (e) => {
    e.preventDefault()
    
    if (!websiteName.trim()) {
      toast.error('Please enter a website name')
      return
    }

    if (!selectedCategory) {
      toast.error('Please select a website category')
      return
    }

    setLoading(true)
    try {
      // Send minimal data to backend - just name and category
      const websiteData = {
        name: websiteName.trim(),
        category: selectedCategory
      }

      console.log('Submitting website creation data:', websiteData)
      
      // Wait for the submit to complete
      await onSubmit(websiteData)
      
      // Reset form only after successful submit
      setWebsiteName('')
      setSelectedCategory('')
      setStep(1)
      onClose()
    } catch (error) {
      console.error('Error creating website:', error)
      const serverMessage = error?.response?.data?.message || error?.message || 'Failed to create website'
      toast.error(serverMessage)
      // Don't reset form or close modal on error, let user try again
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
          <h2 className="text-2xl font-bold text-gray-900">Create New Website</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiX className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Website Category</h3>
                <p className="text-gray-600 text-sm mb-6">
                  Choose a category that best matches your needs. You'll get a pre-built template with relevant sections.
                </p>
              </div>

              {/* Categories Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
                {categories.map((category) => (
                  <button
                    key={category.value}
                    onClick={() => handleCategorySelect(category.value)}
                    className={`p-4 rounded-lg border-2 transition-all text-center ${
                      selectedCategory === category.value
                        ? 'border-primary-600 bg-primary-50'
                        : 'border-gray-200 hover:border-primary-300 bg-white'
                    }`}
                  >
                    <div className="text-3xl mb-2">{category.icon}</div>
                    <p className="font-medium text-sm text-gray-900">{category.label}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <form onSubmit={handleCreateWebsite} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Website Name
                </label>
                <input
                  type="text"
                  value={websiteName}
                  onChange={(e) => setWebsiteName(e.target.value)}
                  placeholder="e.g., My Music Store, John's Portfolio"
                  className="input-field w-full"
                  autoFocus
                  required
                />
                <p className="text-xs text-gray-500 mt-2">
                  This will be used in your website URL and dashboard.
                </p>
              </div>

              {/* Category Info */}
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <p className="text-sm text-blue-900">
                  <strong>Category:</strong> {categories.find(c => c.value === selectedCategory)?.label}
                </p>
                <p className="text-xs text-blue-700 mt-2">
                  Your website will include sections specifically designed for {categories.find(c => c.value === selectedCategory)?.label.toLowerCase()}.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={loading || !websiteName.trim()}
                  className="flex-1 py-2 px-4 bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <FiLoader className="w-4 h-4 animate-spin" /> Creating...
                    </>
                  ) : (
                    'Create Website'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
